import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { usePublicFacility } from '../context/PublicFacilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

/**
 * Post-payment landing page.
 *
 * Lives at /rent/thanks on the FACILITY SUBDOMAIN (e.g.
 * {facility}.mysms360.com/rent/thanks) so the tenant stays on the
 * operator-branded surface. Stripe Checkout's success_url points here.
 *
 * Facility-name resolution cascade (locked 2026-06-01):
 *   1. usePublicFacility() context (set when tenant navigated through
 *      the rent wizard normally on the same SPA session)
 *   2. fallback: derive subdomain slug from the hostname and fetch
 *      /api/rent/by-slug/{slug} (this is the path Stripe redirects take
 *      because Checkout re-loads the page from scratch, so the SPA
 *      context is empty)
 *
 * Per-facility brand styling:
 * Operators fall through to the neutral DEFAULT_BRAND block. The optional
 * BRAND_MAP (keyed by subdomain slug so it's resilient to facility
 * renames) can hold per-facility overrides as they onboard.
 *
 * No marketing chrome, no "Sign Up Free" CTAs — the tenant just paid
 * their local storage operator and they have no relationship to mySMS360
 * the platform.
 *
 * Side effects after payment fire from the Stripe webhook on the backend
 * (rental flips to Active, unit flips Occupied, consolidated welcome
 * email + gate code sent). This page is purely the "you're done" UX.
 */

// Optional per-facility brand overrides, keyed by subdomain slug and
// sourced from the facility's own wordmark + CTA. Empty by default —
// operators fall through to DEFAULT_BRAND below. Add an entry here only
// when a facility supplies custom brand styling.
const BRAND_MAP = {};

// Aliases for facilities whose subdomain doesn't match the canonical
// BRAND_MAP key (e.g. a legacy subdomain that should resolve to the
// same brand entry). Empty by default.
const SLUG_ALIASES = {};

const DEFAULT_BRAND = {
  primary: '#2563EB', // blue-600 — neutral SaaS default
  hero_bg: '#F8FAFC', // slate-50 — keep it clean for unknown brands
  accent: '#DBEAFE', // blue-100
  cta: '#2563EB',
  cta_hover: '#1D4ED8',
  font_family: 'system-ui, -apple-system, sans-serif',
  logo: { kind: 'plain' }, // falls back to facility.name
};

const resolveBrand = (slug) => {
  if (!slug) return DEFAULT_BRAND;
  const key = SLUG_ALIASES[slug] || slug;
  return BRAND_MAP[key] || DEFAULT_BRAND;
};

const BrandedLogo = ({ brand, fallbackName }) => {
  const { logo, primary } = brand;
  if (logo.kind === 'stacked') {
    return (
      <div className="flex flex-col leading-none">
        <span
          className="text-2xl sm:text-3xl font-extrabold tracking-tight"
          style={{ color: primary }}
        >
          {logo.primary_text}
        </span>
        <span className="mt-1 text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-slate-500 uppercase">
          {logo.subtitle}
        </span>
      </div>
    );
  }
  if (logo.kind === 'inline') {
    return (
      <div className="text-xl sm:text-2xl font-bold">
        <span style={{ color: primary }}>{logo.primary_text}</span>
        <span className="text-slate-800">{logo.suffix}</span>
      </div>
    );
  }
  // plain / default — just the facility name
  return (
    <h1 className="text-lg font-semibold text-slate-900">
      {fallbackName}
    </h1>
  );
};

const RentThanks = () => {
  const ctxFacility = usePublicFacility() || {};
  const [fetched, setFetched] = useState(null);
  const [slug, setSlug] = useState(null);

  // Fallback fetch: Stripe redirect lands here from a fresh page load,
  // so the SPA-scoped PublicFacilityContext is empty. Derive the slug
  // from the subdomain and pull the facility doc directly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname || '';
    const parts = host.split('.');
    if (parts.length < 3) return;
    const s = parts[0];
    if (!s || s === 'app' || s === 'www') return;
    setSlug(s);
    if (ctxFacility?.name || fetched !== null) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/rent/by-slug/${s}`);
        setFetched(r.data?.facility || null);
      } catch {
        setFetched(null);
      }
    })();
  }, [ctxFacility?.name, fetched]);

  const facility = (ctxFacility?.name ? ctxFacility : fetched) || {};
  const facilityName = facility?.name || 'your storage facility';
  const facilityPhone = facility?.phone;
  const facilityEmail = facility?.email;
  const portalUrl = '/portal'; // same-origin tenant portal on the subdomain

  const brand = useMemo(() => resolveBrand(slug), [slug]);

  return (
    <div
      className="min-h-screen bg-slate-50"
      style={{ fontFamily: brand.font_family }}
    >
      {/* Header — brand wordmark, address subline */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <BrandedLogo brand={brand} fallbackName={facilityName} />
          {facility?.address && (
            <p className="mt-1 text-[12px] text-slate-500">
              {facility.address}
              {facility.city ? `, ${facility.city}` : ''}
              {facility.state ? `, ${facility.state}` : ''}
              {facility.zip_code ? ` ${facility.zip_code}` : ''}
            </p>
          )}
        </div>
      </header>

      {/* Brand hero band — thin colored strip that ties the page back to
          the marketing site the tenant just came from. */}
      <div
        className="h-2"
        style={{
          background: `linear-gradient(90deg, ${brand.primary} 0%, ${brand.cta} 100%)`,
        }}
      />

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          {/* Universal success cue — keep the green checkmark regardless
              of brand. Tenants read this as "transaction confirmed". */}
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>

          <h2 className="text-2xl font-bold text-slate-900">
            Payment received — welcome to {facilityName}
          </h2>

          <p className="mt-2 text-slate-600">
            Your move-in is confirmed. You'll receive an email shortly with:
          </p>

          <ul className="mt-4 text-left max-w-md mx-auto space-y-1 text-slate-700">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              <span>Your gate access code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              <span>Your signed lease agreement (PDF)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              <span>Payment receipt</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              <span>Facility access hours &amp; rules</span>
            </li>
          </ul>

          <p className="mt-4 text-sm text-slate-500">
            Didn't receive the email within 10 minutes? Check your spam
            folder
            {facilityPhone || facilityEmail ? ', then contact ' : '.'}
            {facilityPhone && (
              <>
                <a
                  href={`tel:${facilityPhone}`}
                  className="hover:underline"
                  style={{ color: brand.cta }}
                >
                  {facilityPhone}
                </a>
                {facilityEmail ? ' or ' : '.'}
              </>
            )}
            {facilityEmail && (
              <a
                href={`mailto:${facilityEmail}`}
                className="hover:underline"
                style={{ color: brand.cta }}
              >
                {facilityEmail}
              </a>
            )}
            {facilityEmail ? '.' : null}
          </p>

          <a
            href={portalUrl}
            className="inline-block mt-4 px-6 py-3 rounded-full text-white font-medium transition-colors"
            style={{ backgroundColor: brand.cta }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = brand.cta_hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = brand.cta;
            }}
          >
            Set up your tenant portal
          </a>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-3">
          A copy of your signed lease and receipt is available in your
          tenant portal at any time.
        </p>
      </main>
    </div>
  );
};

export default RentThanks;
